import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import PickupSummaryService from "../app/data/pickup/pickupSummaryService";
import {
  TerminalViewPickupSummaryQueryParams,
  CustomerViewPickupSummaryQueryParams,
} from "../app/data/pickup/models";
import { PageResponseModel } from "../app/data/common/models";

const pickupSummaryService = PickupSummaryService.getInstance();

export type PickupSummaryListQueryOptions = {
  enabled?: boolean;
};

// const getSummaryNextPageParam = <T,>(lastPage: { data: PageResponseModel<T> } | null | undefined) => {
//   if (!lastPage?.data) {
//     return undefined;
//   }
//   const pageData = lastPage.data;
//   const currentPage =
//     pageData.pageable?.pageNumber !== undefined && pageData.pageable?.pageNumber !== null
//       ? pageData.pageable.pageNumber
//       : pageData.number;
//   if (currentPage === undefined || currentPage === null) {
//     return undefined;
//   }
//   if (typeof pageData.totalPages === "number" && currentPage + 1 >= pageData.totalPages) {
//     return undefined;
//   }
//   if (pageData.last === true) {
//     return undefined;
//   }
//   return currentPage + 1;
// };




const getSummaryNextPageParam = <T,>(lastPage: { data: PageResponseModel<T> | any } | null | undefined) => {
  if (!lastPage?.data) {
    return undefined;
  }

  const pageData = lastPage.data;

  const currentPage = 
    pageData.pageable?.pageNumber ?? 
    pageData.number ??               
    pageData.page;                   

  if (currentPage === undefined || currentPage === null) {
    return undefined;
  }

  if (pageData.last === true) {
    return undefined;
  }

  const totalPages = pageData.totalPages;
  
  if (typeof totalPages === "number") {
    
    if (currentPage + 1 < totalPages) {
      return currentPage + 1;
    }
    return undefined;
  }

  return undefined;
};


export const usePickupSummary = (
  filter: TerminalViewPickupSummaryQueryParams,
  options?: PickupSummaryListQueryOptions
) => {
  return useInfiniteQuery({
    queryKey: ["GET_PICKUP_SUMMARY", filter] as const,
    queryFn: async ({ pageParam = 0 }) =>
      pickupSummaryService.getPickupSummaryList(filter, pageParam as number),
    getNextPageParam: getSummaryNextPageParam,
    enabled: options?.enabled ?? true,
    staleTime: 1000 * 60 * 5,
  });
};

export const usePickupSummaryTiles = (
  filter: TerminalViewPickupSummaryQueryParams,
  options?: PickupSummaryListQueryOptions
) => {
  return useQuery({
    queryKey: ["GET_PICKUP_SUMMARY_TILES", filter],
    queryFn: () => pickupSummaryService.getPickupSummaryTiles(filter),
    select: (res) => res.data,
    staleTime: 1000 * 60 * 5,
    enabled: options?.enabled ?? true,
  });
};

export const useOriginTerminals = (search: string) => {
  return useQuery({
    queryKey: ["GET_ORIGIN_TERMINALS", search],
    queryFn: () => pickupSummaryService.fetchOriginTerminals(search),
    keepPreviousData: true,
    staleTime: 1000 * 60 * 5,
  });
};

export const useDestinationTerminals = (search: string) => {
  return useQuery({
    queryKey: ["GET_DESTINATION_TERMINALS", search],
    queryFn: () => pickupSummaryService.fetchDestinationTerminals(search),
    keepPreviousData: true,
    staleTime: 1000 * 60 * 5,
  });
};

export const useTerminalViewPickupSummaryDetail = (
  terminalViewNumber: number,
  filter?: TerminalViewPickupSummaryQueryParams,
  options?: PickupSummaryListQueryOptions
) => {
  return useInfiniteQuery({
    queryKey: ["GET_TERMINAL_VIEW_PICKUPSUMMARY_DETAIL", terminalViewNumber, filter] as const,
    queryFn: async ({ pageParam = 0 }) =>
      pickupSummaryService.getTerminalViewPickupSummaryList(terminalViewNumber, filter, pageParam as number),
    getNextPageParam: getSummaryNextPageParam,
    enabled: (options?.enabled ?? true) && !!terminalViewNumber,
    staleTime: 1000 * 60 * 5,
  });
};

//customerview hooks

export const useCustomerViewPickupSummary = (
  filter: CustomerViewPickupSummaryQueryParams,
  options?: PickupSummaryListQueryOptions
) => {
  return useInfiniteQuery({
    queryKey: ["GET_CUSTOMER_PICKUP_SUMMARY", filter] as const,
    queryFn: async ({ pageParam = 0 }) =>
      pickupSummaryService.getCustomerViewPickupSummaryList(filter, pageParam as number),
    getNextPageParam: getSummaryNextPageParam,
    enabled: options?.enabled ?? true,
    staleTime: 1000 * 60 * 5,
  });
};

export const useCustomerViewPickupSummaryTiles = (
  filter: CustomerViewPickupSummaryQueryParams,
  options?: PickupSummaryListQueryOptions
) => {
  return useQuery({
    queryKey: ["GET_CUSTOMER_PICKUP_SUMMARY_TILES", filter],
    queryFn: () => pickupSummaryService.getCustomerViewPickupSummaryTiles(filter),
    select: (res) => res.data,
    staleTime: 1000 * 60 * 5,
    enabled: options?.enabled ?? true,
  });
};

export const useRequesterCompany = () => {
  return useQuery({
    queryKey: ["GET_REQUESTED_COMPANY"],
    queryFn: () => pickupSummaryService.fetchRequesterCompany(),
    keepPreviousData: true,
    staleTime: 1000 * 60 * 5,
  });
};

export const useCustomerViewPickupSummaryDetail = (
  requesterCompanyId: string | number,
  filter?: CustomerViewPickupSummaryQueryParams,
  options?: PickupSummaryListQueryOptions
) => {
  return useInfiniteQuery({
    queryKey: ["GET_CUSTOMER_VIEW_PICKUPSUMMARY_DETAIL", String(requesterCompanyId), filter] as const,
    queryFn: async ({ pageParam = 0 }) =>
      pickupSummaryService.getCustomerViewPickupDetailList(requesterCompanyId, filter, pageParam as number),
    getNextPageParam: getSummaryNextPageParam,
    enabled:
      (options?.enabled ?? true) && requesterCompanyId !== null && requesterCompanyId !== undefined && `${requesterCompanyId}` !== "",
    staleTime: 1000 * 60 * 5,
  });
};

export const useDownloadCSVTerminalView = () => {
  return useMutation({
    mutationFn: (filter: TerminalViewPickupSummaryQueryParams) =>
      pickupSummaryService.getDownloadCSVTerminalView(filter),
  });
};


export const useDownloadCSVCustomerView = () => {
  return useMutation({
    mutationFn: (filter: CustomerViewPickupSummaryQueryParams) =>
      pickupSummaryService.getDownloadCSVCustomerView(filter),
  });
};